import { useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import {
  Alert,
  Button,
  Card,
  Eyebrow,
  Field,
  Heading,
  Input,
  Lead,
  Section,
} from '../components/ui';
import { ApiError, api } from '../lib/api';
import { COMPANY } from '../lib/company';
import { usePageTitle } from '../lib/usePageTitle';

/**
 * The dropdown, and a wire contract with the API.
 *
 * Each string must match a member of `ContactTopic` in
 * `apps/api/src/wave_api/schemas/contact.py` exactly, because the API maps the
 * topic to the inbox that receives the enquiry - general and plan questions to
 * hello@, security reviews to security@, existing customers to support@.
 *
 * Editing one list without the other does not misroute anything: the API
 * refuses an unknown topic with a 422 and the page shows the error. Adding a
 * topic means adding it in both places, and deciding there which inbox answers
 * it.
 */
const TOPICS = [
  'General enquiry',
  'Premium / enterprise plan',
  'Open-source agent setup',
  'Security review',
  'Support',
] as const;

const MAILTO_SALES = 'mailto:' + COMPANY.email.sales;

export function Contact() {
  usePageTitle('Contact — WaveQA');

  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [company, setCompany] = useState('');
  const [topic, setTopic] = useState<string>(TOPICS[0]);
  const [message, setMessage] = useState('');

  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);
  const [sent, setSent] = useState(false);

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setBusy(true);
    setError(null);

    try {
      await api.sendContactMessage({
        name,
        email,
        company: company || null,
        topic,
        message,
      });
      setSent(true);
    } catch (err) {
      // The typed error carries the API's own wording for the cases a sender
      // can act on — a rejected address, a message under the minimum length, a
      // rate limit — so it is shown rather than replaced with a generic line.
      setError(
        err instanceof ApiError
          ? err
          : new ApiError(
              'Could not reach the server. Check your connection and try again.',
              0,
            ),
      );
    } finally {
      setBusy(false);
    }
  }

  function fieldError(field: string): string[] | undefined {
    return error?.fields?.[field];
  }

  return (
    <>
      <Section className="border-b border-hull-800">
        <Eyebrow>Contact</Eyebrow>
        <Heading level={1} className="text-white">
          Talk to us
        </Heading>
        <Lead className="mt-5 max-w-2xl">
          Questions about pricing, enterprise deployment, security review, or the
          professional setup service.
        </Lead>
        <Lead className="mt-4 max-w-2xl">
          Already a customer with something to fix, or a question about a charge?{' '}
          <Link
            to="/support"
            className="text-surge-400 underline underline-offset-2 hover:text-surge-300"
          >
            Support
          </Link>{' '}
          is the faster route.
        </Lead>
      </Section>

      <Section>
        <div className="grid gap-6 lg:grid-cols-[1.2fr_1fr]">
          <Card>
            {sent ? (
              <Alert tone="success">
                <p className="font-semibold text-white">
                  Thanks — your message is on its way.
                </p>
                <p className="mt-1.5">
                  We have sent a copy to {email} so you have a record of what you
                  wrote. We usually reply within one business day. If nothing
                  arrives, check your spam folder or write to{' '}
                  <a href={MAILTO_SALES} className="underline">
                    {COMPANY.email.sales}
                  </a>{' '}
                  directly.
                </p>
              </Alert>
            ) : (
              <form onSubmit={handleSubmit} noValidate>
                {error && (
                  <div className="mb-4">
                    <Alert tone="error">{error.message}</Alert>
                  </div>
                )}

                <Field label="Your name" htmlFor="name" errors={fieldError('name')}>
                  <Input
                    id="name"
                    name="name"
                    autoComplete="name"
                    required
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    invalid={Boolean(fieldError('name'))}
                  />
                </Field>

                <Field label="Work email" htmlFor="email" errors={fieldError('email')}>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    autoComplete="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    invalid={Boolean(fieldError('email'))}
                  />
                </Field>

                <Field label="Company" htmlFor="company" hint="Optional">
                  <Input
                    id="company"
                    name="company"
                    autoComplete="organization"
                    value={company}
                    onChange={(e) => setCompany(e.target.value)}
                  />
                </Field>

                <div className="mb-4">
                  <label
                    htmlFor="topic"
                    className="mb-1.5 block text-sm font-medium text-hull-200"
                  >
                    Topic
                  </label>
                  <select
                    id="topic"
                    name="topic"
                    value={topic}
                    onChange={(e) => setTopic(e.target.value)}
                    className="w-full rounded-lg border border-hull-600 bg-hull-950 px-3.5 py-2.5 text-sm text-white"
                  >
                    {TOPICS.map((topic) => (
                      <option key={topic}>{topic}</option>
                    ))}
                  </select>
                </div>

                <div className="mb-4">
                  <label
                    htmlFor="message"
                    className="mb-1.5 block text-sm font-medium text-hull-200"
                  >
                    Message
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    rows={5}
                    required
                    minLength={10}
                    maxLength={4000}
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    className="w-full rounded-lg border border-hull-600 bg-hull-950 px-3.5 py-2.5 text-sm text-white placeholder:text-hull-500"
                    placeholder="What would you like to know?"
                  />
                  {fieldError('message') && (
                    <p className="mt-1.5 text-sm text-fail-400">
                      {fieldError('message')?.join(' ')}
                    </p>
                  )}
                </div>

                <Button type="submit" disabled={busy}>
                  {busy ? 'Sending…' : 'Send message'}
                </Button>

                <p className="mt-3 text-xs text-hull-400">
                  Please do not include passwords, API keys or other secrets in
                  this message.
                </p>
              </form>
            )}
          </Card>

          <div className="space-y-4">
            <Card>
              <h2 className="text-base font-semibold text-white">Enterprise and Premium</h2>
              <p className="mt-2 text-sm leading-relaxed text-hull-300">
                Larger agent capacity, private deployment, custom integrations,
                security review and SLA options are handled per engagement.
              </p>
            </Card>

            <Card>
              <h2 className="text-base font-semibold text-white">Security disclosure</h2>
              <p className="mt-2 text-sm leading-relaxed text-hull-300">
                If you believe you have found a vulnerability, report it
                privately to{' '}
                <a
                  href="mailto:security@waveqa-agent.com"
                  className="text-surge-400 underline hover:text-surge-300"
                >
                  security@waveqa-agent.com
                </a>{' '}
                rather than through this form. A published disclosure policy
                with a PGP key is part of the launch checklist.
              </p>
            </Card>
          </div>
        </div>
      </Section>
    </>
  );
}
